import React, { useState, useEffect, useRef } from 'react';
import okn from "./Images/ok_n.gif"
import canceln from "./Images/cancel_n.gif"
import masterBG from "../../Common/Images/MasterBKG.png"
import oko from "./Images/ok_o.gif"
import cancelo from "./Images/cancel_o.gif"
// --- Interfaces ---
interface LoginData {
    txtLogin: string;
    txtPwd: string;
    txtFName: string;
    txtLName: string;
    txtEMail: string;
    rdoGender: string; // 'M' or 'F'
    rdoStatus: string; // 'A', 'I', 'D'
    CalEXP: string; // Date string YYYY-MM-DD
    UserCategory: string;
    Curriculum: string;
    LoginCategory: string;
}

const EL_EditLogin: React.FC = () => {
    // --- Query Params ---
    const queryParams = new URLSearchParams(window.location.search);
    const SchoolCode = queryParams.get('SchoolCode') || '';
    const FamilyCode = queryParams.get('FamilyCode') || '';
    const LoginParam = queryParams.get('Login') || '';
    const LoginType = queryParams.get('LoginType') || '';
    const CalledFrom = queryParams.get('CalledFrom') || '';
    const API_BASE = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';
    // --- State ---
    const [formData, setFormData] = useState<LoginData>({
        txtLogin: LoginParam,
        txtPwd: '',
        txtFName: '',
        txtLName: '',
        txtEMail: '',
        rdoGender: '',
        rdoStatus: '', // Default Active
        CalEXP: new Date().toISOString().split('T')[0],
        UserCategory: '',
        Curriculum: '',
        LoginCategory: ''
    });

    // Permissions (chk1 to chk20) - Mapped to array indices 0-19
    const [permissions, setPermissions] = useState<boolean[]>(new Array(20).fill(false));

    // More Details
    const [moreDetails, setMoreDetails] = useState({
        chkCanAccessLessonPlan: false,
        chkCanEarnPoints: false,
        chkEligibleForIncentives: false
    });

    const [showPerm, setShowPerm] = useState(false);
    const [showMoreDetails, setShowMoreDetails] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // --- Styles ---
    const styles = {
        body: {
            margin: '2px', fontFamily: 'Arial', fontSize: '10pt', backgroundColor: '#214461',
            minHeight: '100vh', display: 'flex', justifyContent: 'center'
        },
        masterBkg: {
            // Assuming image is in public/Images
            backgroundImage: `url(${masterBG})`,
            width: '800px', height: '580px', overflow: 'hidden',
            position: 'relative' as const
        },
        innerTable: {
            backgroundColor: 'AliceBlue', width: '90%', margin: '0 auto'
        },
        header: {
            fontSize: '14pt', textDecoration: 'underline', height: '30px', verticalAlign: 'top'
        },
        labelCol: { width: '35%', textAlign: 'right' as const, verticalAlign: 'middle' as const, padding: '4px' },
        valCol: { width: '65%', textAlign: 'left' as const, verticalAlign: 'middle' as const, color: 'Blue', fontWeight: 'bold' as const, padding: '4px' },
        textBox: {
            width: '200px', color: 'blue', borderColor: '#003300', borderStyle: 'solid', borderWidth: '1px'
        },
        btn: { cursor: 'pointer', border: 'none', backgroundColor: 'transparent', margin: '0 10px' }
    };

    // --- Load Data ---
    useEffect(() => {
        // Visibility Logic based on VB Page_Load
        let permVisible = false;
        let detailsVisible = false;

        if (LoginParam === '') permVisible = true;

        if (CalledFrom === 'L') {
            permVisible = true;
            detailsVisible = false;
        } else {
            permVisible = false;
            detailsVisible = false;
        }

        // Simulate Session Check (In real app, fetch from context/API)
        // if (isSysAdmin) permVisible = false; 

        setShowPerm(permVisible);
        setShowMoreDetails(detailsVisible);

        const fetchData = async () => {
            if (!LoginParam) {
                setFormData(prev => ({ ...prev, UserCategory: 'Admin User' }));
                return;
            }

            try {
                const url = `${API_BASE}api/edit-login-get-details/?SchoolCode=${SchoolCode}&FamilyCode=${FamilyCode}&Login=${LoginParam}&LoginType=${LoginType}`;
                const res = await fetch(url);
                const json = await res.json();

                if (json.error) {
                    setErrorMsg(json.error);
                } else if (json.data) {
                    const d = json.data;
                    setFormData({
                        txtLogin: LoginParam,
                        txtPwd: d.Password || '',
                        txtFName: d.FName || '',
                        txtLName: d.LName || '',
                        txtEMail: d.Email || '',
                        rdoGender: d.Gender || '',
                        rdoStatus: d.ActivityStatus === 'D' ? 'D' : (d.ActivityStatus === 'A' ? 'A' : 'I'),
                        CalEXP: d.ExpiryDate ? new Date(d.ExpiryDate).toISOString().split('T')[0] : '',
                        UserCategory: d.UserCategory || '',
                        Curriculum: d.Curriculum || '',
                        LoginCategory: d.LoginCategory || ''
                    });

                    // Parse Permissions String (e.g. "YNYY...")
                    if (d.Permissions) {
                        const pStr = d.Permissions;
                        const newPerms = [...permissions];
                        for (let i = 0; i < 20; i++) {
                            newPerms[i] = pStr[i] === 'Y';
                        }
                        setPermissions(newPerms);
                    }
                }
            } catch (err) {
                console.error(err);
                setErrorMsg('Failed to load data');
            }
        };

        fetchData();
    }, [LoginParam]);

    // --- Handlers ---

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleRadio = (group: string, val: string) => {
        setFormData(prev => ({ ...prev, [group]: val }));
    };

    const togglePermission = (index: number) => {
        const newPerms = [...permissions];
        newPerms[index] = !newPerms[index];
        setPermissions(newPerms);
    };

    const handleSave = async () => {
        const payload = {
            Mode: LoginParam ? 'Edit' : 'Add',
            SchoolCode,
            FamilyCode: LoginParam ? FamilyCode : 'AA', // Default if Add
            Login: formData.txtLogin,
            ...formData,
            Permissions: permissions,
            // MoreDetails would be mapped here if backend supported it in this migration scope
        };

        try {
            const res = await fetch(`${API_BASE}api/edit-login-save/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const json = await res.json();

            if (json.status === 'OK') {
                handleReturn();
            } else {
                alert("Error: " + json.error);
            }
        } catch (err) {
            alert("Save failed");
        }
    };

    const handleReturn = () => {
        if (CalledFrom === 'L') {
            window.location.href = '/WebAdmin/ADMIN/EL_ListUsers';
        } else if (CalledFrom === 'TA') {
            window.location.href = '/WebAdmin/ADMIN/EL_ListTAUsers';
        }else {
            window.location.href = '/SysAdmin';
        }
    };

    // --- Render ---

    // Permission Labels mapped to index
    const permLabels = [
        "Manage Schools", "Manage Packages", "Manage Dic Files", "Manage Products", "Manage Key words",
        "Set site status", "Manage Weekly Tips", "Customer Support", "View Structured reports", "Login as another user",
        "Manage Topics", "Manage Vouchers", "Manage MW-MD Link", "View MD Questions", "View MD Exceptions",
        "Post Qn Category", "Manage Roles", "Total Sales Report", "Write Reports", "Add New Distributor"
    ];

    return (
        <div style={styles.body}>
            <div style={styles.masterBkg}>
                <div id="divBlank" style={{ height: '120px' }}></div>

                {errorMsg && <div style={{ color: 'red', textAlign: 'center' }}>{errorMsg}</div>}

                <table style={styles.innerTable} cellSpacing={0} cellPadding={4}>
                    <tbody>
                        <tr>
                            <td colSpan={2} style={styles.header}>Edit Login Details</td>
                        </tr>
                        <tr>
                            {/* --- Left Column: Details --- */}
                            <td align="left" valign="top" width="52%">
                                <table border={0} cellSpacing={0} cellPadding={4}>
                                    <tbody>
                                        <tr>
                                            <td style={styles.labelCol}>School Code:</td>
                                            <td style={styles.valCol}>{SchoolCode}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>Login:</td>
                                            <td style={styles.valCol}>
                                                <input
                                                    type="text" id="txtLogin"
                                                    maxLength={50} style={styles.textBox}
                                                    value={formData.txtLogin}
                                                    onChange={handleInput}
                                                    readOnly={!!LoginParam}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>User Category:</td>
                                            <td style={styles.valCol}>{formData.UserCategory}</td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>Password:</td>
                                            <td style={{ ...styles.valCol, color: 'black', fontWeight: 'normal' }}>
                                                <input type="password" id="txtPwd" maxLength={50} style={styles.textBox} value={formData.txtPwd} onChange={handleInput} />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>First Name:</td>
                                            <td style={{ ...styles.valCol, color: 'black', fontWeight: 'normal' }}>
                                                <input type="text" id="txtFName" maxLength={30} style={styles.textBox} value={formData.txtFName} onChange={handleInput} />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>Last Name:</td>
                                            <td style={{ ...styles.valCol, color: 'black', fontWeight: 'normal' }}>
                                                <input type="text" id="txtLName" maxLength={30} style={styles.textBox} value={formData.txtLName} onChange={handleInput} />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>Gender:</td>
                                            <td style={{ ...styles.valCol, color: 'blue', fontWeight: 'normal' }}>
                                                <label><input type="radio" name="rdoGender" checked={formData.rdoGender === 'M' || formData.rdoGender === 'B'} onChange={() => handleRadio('rdoGender', 'M')} /> Male</label>
                                                &nbsp;&nbsp;&nbsp;&nbsp;
                                                <label><input type="radio" name="rdoGender" checked={formData.rdoGender === 'F' || formData.rdoGender === 'G'} onChange={() => handleRadio('rdoGender', 'F')} /> Female</label>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>Status:</td>
                                            <td style={{ ...styles.valCol, color: 'blue', fontWeight: 'normal' }}>
                                                <label><input type="radio" name="rdoStatus" checked={formData.rdoStatus === 'A'} onChange={() => handleRadio('rdoStatus', 'A')} /> Active</label>
                                                &nbsp;&nbsp;&nbsp;&nbsp;
                                                <label><input type="radio" name="rdoStatus" checked={formData.rdoStatus === 'I'} onChange={() => handleRadio('rdoStatus', 'I')} /> Inactive</label>
                                                &nbsp;&nbsp;&nbsp;&nbsp;
                                                <label><input type="radio" name="rdoStatus" checked={formData.rdoStatus === 'D'} onChange={() => handleRadio('rdoStatus', 'D')} /> Deleted</label>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>Expiry Date:</td>
                                            <td style={{ ...styles.valCol, color: 'black', fontWeight: 'normal' }}>
                                                {/* Custom Calendar Replacment */}
                                                <input
                                                    type="date" id="CalEXP"
                                                    value={formData.CalEXP}
                                                    onChange={handleInput}
                                                    style={{
                                                        color: 'blue', backgroundColor: 'aliceblue', width: '130px',
                                                        fontSize: '10pt', border: '1px solid #7f9db9'
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>eMail ID:</td>
                                            <td style={{ ...styles.valCol, color: 'black', fontWeight: 'normal' }}>
                                                <input type="text" id="txtEMail" maxLength={200} style={styles.textBox} value={formData.txtEMail} onChange={handleInput} />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={styles.labelCol}>Curriculum(s):</td>
                                            <td style={{ ...styles.valCol, fontWeight: 'normal' }}>{formData.Curriculum}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>

                            {/* --- Middle Column: Permissions --- */}
                            {/* <td align="left" valign="top" width="48%" style={{ display: showPerm ? 'table-cell' : 'none' }}>
                                <table border={0} cellSpacing={0} cellPadding={4} width="100%">
                                    <tbody>
                                        <tr>
                                            <td align="center" valign="middle" colSpan={2} style={{ textDecoration: 'underline' }}>
                                                This SysAdmin user can:
                                            </td>
                                        </tr>
                                        {Array.from({ length: 10 }).map((_, i) => (
                                            <tr key={i}>
                                                <td align="left" width="50%">
                                                    <label>
                                                        <input type="checkbox" checked={permissions[i]} onChange={() => togglePermission(i)} /> {permLabels[i]}
                                                    </label>
                                                </td>
                                                <td align="left" width="50%">
                                                    <label>
                                                        <input type="checkbox" checked={permissions[i + 10]} onChange={() => togglePermission(i + 10)} /> {permLabels[i + 10]}
                                                    </label>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </td> */}

                            {/* --- Right Column: More Details (Placeholder for layout fidelity) --- */}
                            {/* <td align="left" valign="top" width="48%" style={{ display: showMoreDetails ? 'table-cell' : 'none' }}>
                                <table border={0} cellSpacing={0} cellPadding={4} width="100%">
                                    <tbody>
                                        <tr><td style={{ textDecoration: 'underline' }}>More Details:</td></tr>
                                        <tr><td><label><input type="checkbox" /> Can Access Lesson Plan</label></td></tr>
                                        <tr><td><label><input type="checkbox" /> Can Earn Points</label></td></tr>
                                        <tr><td><label><input type="checkbox" /> Eligible For Incentives</label></td></tr>
                                    </tbody>
                                </table>
                            </td> */}
                        </tr>

                        {/* --- Buttons --- */}
                        <tr>
                            <td colSpan={2} align="center">
                                <img
                                    src={okn}
                                    alt="OK"
                                    style={styles.btn}
                                    onMouseOver={(e) => (e.currentTarget.src = oko)}
                                    onMouseOut={(e) => (e.currentTarget.src = okn)}
                                    // onMouseDown={(e) => (e.currentTarget.src = okd)}
                                    onClick={handleSave}
                                />

                                <img
                                    src={canceln}
                                    alt="Cancel"
                                    style={styles.btn}
                                    onMouseOver={(e) => (e.currentTarget.src = cancelo)}
                                    onMouseOut={(e) => (e.currentTarget.src = canceln)}
                                    // onMouseDown={(e) => (e.currentTarget.src = canceld)}
                                    onClick={handleReturn}
                                />
                            </td>

                        </tr>
                        <tr><td colSpan={2} height="10px"></td></tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EL_EditLogin;